import React from 'react';
import { ScrollView, View } from 'react-native';

import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "Balanza de ingresos" (por comunidad, por mes).
// Matriz: filas = partidas/conceptos, columnas = métricas financieras.
//
// TODO(backend): aún no hay endpoint ni dónde se guarda esta info.
// Por ahora se maqueta con datos demo hardcodeados (Comunidad Real Bilbao, Mayo 2026).
// Los tipos viven aquí encapsulados; se abstraerán cuando exista el contrato del API.
// ───────────────────────────────────────────────────────────────────────────

// Una partida del fraccionamiento (Control de acceso, Mantenimiento, etc.).
type BalanzaConcept = {
  name: string;
  // Ingresos
  cuota: number;                  // cuota mensual por vivienda de esta partida
  aportacionIdealVecinos: number; // lo que idealmente aportan los vecinos
  aportacionVinte: number;        // lo que aporta VINTE (viviendas en inventario)
  ingresoIdealEsperado: number;   // = aportacionIdealVecinos + aportacionVinte
  cobroPuntual: number;           // cobrado al corriente
  cobroAtrasado: number;          // cobrado de adeudos previos
  ingresoRealTotal: number;       // ingreso real del periodo
  conservacion: number;           // monto a conservación
  proyeccionVobo: number;         // proyección Vo.bo.
  // Presupuesto / egresos
  pptoOperacionReal: number;      // presupuesto de operación real
  presupuestoControl: number;     // presupuesto de control (acumulado)
  presupuestoControlPct: number;  // % ejercido del presupuesto de control
  gastoRealMes: number;           // gasto real del mes
  pagoAdeudos: number;            // pago de adeudos (puede ser negativo)
  diferencia: number;             // diferencia ingreso vs gasto
  pasivoAcFondo: number;          // pasivo de la AC y/o fondo (puede ser negativo)
};

type BalanzaReport = {
  communityName: string;
  periodo: string;
  viviendasEnOperacion: number;
  clientesQuePagaron: number;
  inventarioVinte: number;
  concepts: BalanzaConcept[];
  mttoMesEnCurso: number;
  estadoCuentaBancario: number;
  montoConservacionAPagar: number;
};

type ConceptNumericKey = Exclude<keyof BalanzaConcept, 'name'>;

// ─── Datos DEMO (basados en la balanza de referencia en Excel) ───────────────
const DEMO: BalanzaReport = {
  communityName: 'Comunidad Real Bilbao A.C',
  periodo: 'Mayo 2026',
  viviendasEnOperacion: 431,
  clientesQuePagaron: 264,
  inventarioVinte: 155,
  mttoMesEnCurso: 75400.0,
  estadoCuentaBancario: 258785.33,
  montoConservacionAPagar: 43746.97,
  concepts: [
    {
      name: 'Control de Acceso',
      cuota: 188.4,
      aportacionIdealVecinos: 65960.32, aportacionVinte: 28202.0, ingresoIdealEsperado: 94162.32,
      cobroPuntual: 49737.6, cobroAtrasado: 31462.8, ingresoRealTotal: 110402.4,
      conservacion: 14201.68, proyeccionVobo: 109282.8,
      pptoOperacionReal: 108364.0, presupuestoControl: 505477.2, presupuestoControlPct: 21,
      gastoRealMes: 108263.0, pagoAdeudos: 101.0, diferencia: 16341.08, pasivoAcFondo: -12062.28,
    },
    {
      name: 'Mantenimiento y Servicios de Conjunto',
      cuota: 198.0,
      aportacionIdealVecinos: 69272.28, aportacionVinte: 29688.12, ingresoIdealEsperado: 98960.4,
      cobroPuntual: 52272.0, cobroAtrasado: 33066.0, ingresoRealTotal: 116028.0,
      conservacion: 43145.05, proyeccionVobo: 76510.03,
      pptoOperacionReal: 55815.35, presupuestoControl: 531234.0, presupuestoControlPct: 11,
      gastoRealMes: 123774.78, pagoAdeudos: -67959.43, diferencia: 7746.78, pasivoAcFondo: -50891.83,
    },
    {
      name: 'Mantenimiento y Servicios de Privada',
      cuota: 116.52,
      aportacionIdealVecinos: 40765.69, aportacionVinte: 17471.01, ingresoIdealEsperado: 58236.7,
      cobroPuntual: 30761.28, cobroAtrasado: 19458.84, ingresoRealTotal: 68280.72,
      conservacion: 99.31, proyeccionVobo: 38520.63,
      pptoOperacionReal: 58137.39, presupuestoControl: 312623.16, presupuestoControlPct: 19,
      gastoRealMes: 96630.7, pagoAdeudos: -38493.31, diferencia: 28349.98, pasivoAcFondo: -28449.29,
    },
    {
      name: 'Administración de Prestador de Servicios',
      cuota: 68.29,
      aportacionIdealVecinos: 23546.39, aportacionVinte: 10584.95, ingresoIdealEsperado: 34131.34,
      cobroPuntual: 18028.56, cobroAtrasado: 11404.43, ingresoRealTotal: 40017.94,
      conservacion: 24912.66, proyeccionVobo: 45096.5,
      pptoOperacionReal: 59044.0, presupuestoControl: 183222.07, presupuestoControlPct: 32,
      gastoRealMes: 59044.0, pagoAdeudos: 0, diferencia: 5886.6, pasivoAcFondo: 5886.6,
    },
    {
      name: 'Administración de la AC',
      cuota: 29.22,
      aportacionIdealVecinos: 10075.06, aportacionVinte: 4529.1, ingresoIdealEsperado: 14604.16,
      cobroPuntual: 7714.08, cobroAtrasado: 4879.74, ingresoRealTotal: 17122.92,
      conservacion: 47777.68, proyeccionVobo: 19038.0,
      pptoOperacionReal: 62381.84, presupuestoControl: 78397.26, presupuestoControlPct: 80,
      gastoRealMes: 16929.01, pagoAdeudos: 45452.83, diferencia: 47971.59, pasivoAcFondo: 47971.59,
    },
  ],
};

// ─── Definición de columnas (orden y formato) ────────────────────────────────
type Col = {
  key: ConceptNumericKey;
  label: string;
  format: 'currency' | 'percent';
  signed?: boolean; // colorea por signo (verde/rojo)
};

const COLUMNS: Col[] = [
  { key: 'cuota', label: 'Cuota', format: 'currency' },
  { key: 'aportacionIdealVecinos', label: 'Aport. Ideal Vecinos', format: 'currency' },
  { key: 'aportacionVinte', label: 'Aport. VINTE', format: 'currency' },
  { key: 'ingresoIdealEsperado', label: 'Ingreso Ideal Esperado', format: 'currency' },
  { key: 'cobroPuntual', label: 'Cobro Puntual', format: 'currency' },
  { key: 'cobroAtrasado', label: 'Cobro Atrasado', format: 'currency' },
  { key: 'ingresoRealTotal', label: 'Ingreso Real Total', format: 'currency' },
  { key: 'conservacion', label: 'Conservación', format: 'currency' },
  { key: 'proyeccionVobo', label: 'Proyección Vo.bo.', format: 'currency' },
  { key: 'pptoOperacionReal', label: 'Ppto Operación Real', format: 'currency' },
  { key: 'presupuestoControl', label: 'Presupuesto Control', format: 'currency' },
  { key: 'presupuestoControlPct', label: '% Ejerc.', format: 'percent' },
  { key: 'gastoRealMes', label: 'Gasto Real del Mes', format: 'currency' },
  { key: 'pagoAdeudos', label: 'Pago Adeudos', format: 'currency', signed: true },
  { key: 'diferencia', label: 'Diferencia', format: 'currency', signed: true },
  { key: 'pasivoAcFondo', label: 'Pasivo AC y/o Fondo', format: 'currency', signed: true },
];

const NAME_COL_W = 220;
const CELL_W = 116;

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

const fmt = (col: Col, v: number) => (col.format === 'percent' ? `${v}%` : money(v));

// Calcula la fila de totales ("Monto de la Cuota") sumando cada columna numérica.
function computeTotals(concepts: BalanzaConcept[]): Record<ConceptNumericKey, number> {
  const acc = {} as Record<ConceptNumericKey, number>;
  for (const col of COLUMNS) acc[col.key] = 0;
  for (const c of concepts) for (const col of COLUMNS) acc[col.key] += c[col.key];
  // El % total no se suma; lo derivamos del presupuesto ejercido.
  acc.presupuestoControlPct = acc.presupuestoControl
    ? Math.round((acc.gastoRealMes / acc.presupuestoControl) * 100)
    : 0;
  return acc;
}

// ─── Celda de la tabla ───────────────────────────────────────────────────────
function Cell({ children, w, header, total, align = 'right', signed, value }: any) {
  const signColor =
    signed && typeof value === 'number'
      ? value < 0 ? 'text-red-600' : value > 0 ? 'text-green-700' : 'text-gray-700'
      : total ? 'text-gray-900' : 'text-gray-700';

  return (
    <View
      style={tw.style(
        `px-2 py-2 justify-center border-r border-b border-gray-100`,
        { width: w },
        header && `bg-secondary/10`,
        total && `bg-gray-50`,
        align === 'right' ? `items-end` : `items-start`,
      )}
    >
      <Text
        style={tw.style(
          `text-xs`,
          header ? `text-secondary font-600 text-center` : signColor,
          total && !signed && `font-700`,
        )}
        numberOfLines={header ? 3 : 1}
      >
        {children}
      </Text>
    </View>
  );
}

function Chip({ label, value }: { label: string; value: string | number }) {
  return (
    <View style={tw`bg-white border border-gray-200 rounded-xl px-4 py-2 mr-3 mb-2`}>
      <Text style={tw`text-gray-400 text-xs`}>{label}</Text>
      <Text style={tw`text-secondary font-600`}>{value}</Text>
    </View>
  );
}

export default function BalanzaIngresos({ communityId, date }: { communityId?: string; date?: Date }) {
  // TODO(backend): cuando exista el endpoint, reemplazar DEMO por useQuery(communityId, date).
  const data = DEMO;
  const totals = computeTotals(data.concepts);

  return (
    <View style={tw`px-4 pb-8`}>
      {/* Encabezado */}
      <View style={tw`bg-white rounded-xl border border-gray-100 px-5 py-4 mb-4`}>
        <View style={tw`row justify-between items-center mb-3`}>
          <Text style={tw`text-lg font-700 text-secondary`}>{data.communityName}</Text>
          <Text style={tw`text-gray-400`}>
            Periodo: <Text style={tw`text-gray-700 font-600`}>{data.periodo}</Text>
          </Text>
        </View>

        <View style={tw`row flex-wrap`}>
          <Chip label="Viviendas en operación" value={data.viviendasEnOperacion} />
          <Chip label="Clientes que pagaron" value={data.clientesQuePagaron} />
          <Chip label="Inventario VINTE" value={data.inventarioVinte} />
        </View>
      </View>

      {/* Tabla (scroll horizontal: es muy ancha) */}
      <ScrollView horizontal showsHorizontalScrollIndicator style={tw`bg-white rounded-xl border border-gray-100`}>
        <View>
          {/* Header */}
          <View style={tw`row border-b border-gray-200`}>
            <Cell w={NAME_COL_W} header align="left">Partida</Cell>
            {COLUMNS.map(col => (
              <Cell key={col.key} w={CELL_W} header>{col.label}</Cell>
            ))}
          </View>

          {/* Filas por partida */}
          {data.concepts.map(c => (
            <View key={c.name} style={tw`row`}>
              <Cell w={NAME_COL_W} align="left">
                <Text style={tw`text-xs text-gray-800 font-500`}>{c.name}</Text>
              </Cell>
              {COLUMNS.map(col => (
                <Cell key={col.key} w={CELL_W} signed={col.signed} value={c[col.key]}>
                  {fmt(col, c[col.key])}
                </Cell>
              ))}
            </View>
          ))}

          {/* Totales: "Monto de la Cuota" */}
          <View style={tw`row border-t-2 border-secondary/40`}>
            <Cell w={NAME_COL_W} total align="left">
              <Text style={tw`text-xs text-secondary font-700`}>Monto de la Cuota</Text>
            </Cell>
            {COLUMNS.map(col => (
              <Cell key={col.key} w={CELL_W} total signed={col.signed} value={totals[col.key]}>
                {fmt(col, totals[col.key])}
              </Cell>
            ))}
          </View>
        </View>
      </ScrollView>

      {/* Pie */}
      <View style={tw`row flex-wrap mt-4`}>
        <Chip label="Mtto del mes en curso" value={money(data.mttoMesEnCurso)} />
        <Chip label="Estado de cuenta bancario" value={money(data.estadoCuentaBancario)} />
        <Chip label="Monto de conservación a pagar" value={money(data.montoConservacionAPagar)} />
      </View>

      {/* <Text style={tw`text-gray-300 text-xs mt-4`}>
        Datos demo · pendiente de backend · communityId: {communityId ?? '—'}
      </Text> */}
    </View>
  );
}
